import { Component, OnInit, Input } from '@angular/core';
import type { EChartsOption } from 'echarts';
import { catchError, debounceTime, Subscription, throwError } from 'rxjs';
import { EthAbsData } from 'src/app/models/eth-abs-data';
import { SyncService } from 'src/app/services/sync-service';
import { FieldDashboardDataService } from '../field-dashboard-data.service';
import { formatDate } from '@angular/common';

@Component({
  selector: 'app-crop-spray-analysis',
  templateUrl: './crop-spray-analysis.component.html',
  styleUrls: ['./crop-spray-analysis.component.css'],
  standalone: false
})
export class CropSprayAnalysisComponent implements OnInit {
  @Input() fieldId: string = '';
  @Input() farmId: string;

  @Input() fromDate: String;
  @Input() toDate: String;

  chartOption: EChartsOption = {};
  hasData: boolean = true;
  isLoading: boolean = true;

  ethAbsData: EthAbsData = {
    cultivars: [],
    legends: [],
    eth: [],
    abs: []
  };

  private subscription: Subscription;

  constructor(
    private syncService: SyncService,
    private dataService: FieldDashboardDataService
  ) {
  }


  ngOnInit(): void {
    this.subscription = this.dataService.fieldData$.pipe(debounceTime(100)).subscribe(data => {
      if (data.fieldId && data.farmId && data.startDate && data.endDate) {
        let fromDate: string;
        let toDate: string;

        if (data.userChangedDates) {
          fromDate = formatDate(data.startDate, 'yyyy-MM-dd', 'en-ZA');
          toDate = formatDate(data.endDate, 'yyyy-MM-dd', 'en-ZA');
        } else if (data.season) {
          const startYear = parseInt(data.season.split('/')[0]);
          fromDate = `${startYear}-06-01`;
          toDate = `${startYear + 1}-05-31`;
        } else {
          return;
        }

        if (this.fieldId === data.fieldId && this.fromDate === fromDate && this.toDate === toDate) return;

        this.fieldId = data.fieldId;
        this.farmId = '2475';
        this.fromDate = fromDate;
        this.toDate = toDate;
        this.populateData();
      }
    });
  }

  populateData() {
    this.isLoading = true;
    this.hasData = true;
    console.log("From date", this.fromDate);
    console.log("To date", this.toDate);
    this.syncService.getEthAbsStats(this.fieldId, this.fromDate, this.toDate).pipe(
      catchError(error => {
        console.error();
        return throwError(() => error);
      })
    ).subscribe((response) => {
      this.ethAbsData = response;
      this.isLoading = false;
      this.loadChartData();
    })
  }

  loadChartData(): void {
    this.hasData = this.ethAbsData.cultivars.length > 0 &&
      (this.ethAbsData.eth.some(v => v !== 0) || this.ethAbsData.abs.some(v => v !== 0));
    if (!this.hasData) return;
    this.chartOption = {
      title: {
        text: 'Eth & Abs / Ha',
        left: 0,
        top: 10,
        textStyle: {
          fontSize: 18,
          fontWeight: 'bold'
        }
      },
      tooltip: {
        trigger: 'axis',
        axisPointer: {
          type: 'shadow'
        }
      },
      legend: {
        data: this.ethAbsData.legends,
        bottom: 0,
        itemGap: 25
      },
      grid: {
        left: '10%',
        right: '5%',
        bottom: '10%',
        top: '25%',
        containLabel: true
      },
      xAxis: {
        type: 'category',
        data: this.ethAbsData.cultivars
      },
      yAxis: {
        type: 'value',
        axisLabel: {
          formatter: '{value}'
        }
      },
      series: [
        this.ethAbsData.eth.some(v => v !== 0) ? {
          name: 'Sum of Eth (L/Ha)',
          type: 'bar' as const,
          data: this.ethAbsData.eth,
          itemStyle: { color: '#5470c6' }
        } : null,
        this.ethAbsData.abs.some(v => v !== 0) ? {
          name: 'Sum of Abs (L/Ha)',
          type: 'bar' as const,
          data: this.ethAbsData.abs,
          itemStyle: { color: '#91cc75' }
        } : null
      ].filter(s => s !== null)
    };
  }
}
